import { useState } from 'react';
import { Badge, TextInput, Tooltip } from '@mantine/core';
import { zVariableValidationType, type ValidationResult } from '../../common/variable/validation';
import { notifications } from '@mantine/notifications';

export function EditableBadge({
  label,
  onChange,
  disabled,
}: {
  label: string;
  onChange: (oldName: string, newName: string) => Promise<void>;
  disabled?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(label);

  const validateVariableName = (name: string): ValidationResult => {
    try {
      zVariableValidationType.parse(name);
      return { isValid: true };
    } catch (error) {
      return {
        isValid: false,
        reason: error instanceof Error ? error.message : 'Invalid variable name',
      };
    }
  };

  const handleSave = async () => {
    if (editValue.trim() === '' || editValue === label) {
      setEditValue(label);
      setIsEditing(false);
      return;
    }

    const validation = validateVariableName(editValue.trim());

    if (!validation.isValid) {
      notifications.show({
        title: 'Invalid variable name',
        message: validation.reason,
        color: 'red',
      });
      setEditValue(label);
    } else {
      await onChange(label, editValue.trim());
    }
    setIsEditing(false);
  };

  return isEditing ?
      <TextInput
        type="text"
        variant="unstyled"
        size="sm"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void handleSave();
          } else if (e.key === 'Escape') {
            setEditValue(label);
            setIsEditing(false);
          }
        }}
        className="h-6 w-full bg-transparent px-1"
        autoFocus
        onFocus={(e) => {
          e.currentTarget.select();
        }}
        onDoubleClick={(e) => {
          e.currentTarget.select();
        }}
      />
    : <Tooltip
        label="Click to edit parameter name"
        disabled={disabled}
      >
        <Badge
          color="gray"
          radius="sm"
          variant="light"
          className={`normal-case ${!disabled ? 'cursor-pointer text-[var(--mantine-color-text)] hover:bg-gray-200 dark:hover:bg-gray-600' : ''}`}
          onClick={() => !disabled && setIsEditing(true)}
        >
          {label}
        </Badge>
      </Tooltip>;
}
